import { Checkbox, HStack } from '@chakra-ui/react';
import { Input } from 'components/ui/form/Input/Input';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { ReactElement } from 'react';
import { DeleteIcon } from 'assets/icons/v2';
import { MCOption } from './TeacherMCBlock';
import { MultipleChoiceBlockProps } from '../MultipleChoiceBlock';

export const TeacherMCOption = ({
  opt,
  i,
  optionRefs,
  toggleOption,
  updateOptionText,
  deleteFocusedOption,
  updateOptions,
  editorContext,
}: {
  opt: MCOption;
  i: number;
  optionRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  toggleOption: (index: number) => void;
  updateOptionText: (index: number, text: string) => void;
  deleteFocusedOption: (index: number) => void;
  updateOptions: (newOptions: MCOption[], focusIndex?: number) => void;
  editorContext: {
    options: MCOption[];
    editor: MultipleChoiceBlockProps['editor'];
    block: MultipleChoiceBlockProps['block'];
  };
}): ReactElement => {
  const { options } = editorContext;

  const focusInput = (index: number, position?: number): void => {
    setTimeout(() => {
      const input = optionRefs.current[index];
      if (input) {
        const cursorPos = position ?? input.value.length;
        input.focus();
        input.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);
  };

  const handleEnterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart ?? 0;
    const currentText = input.value;
    const left = currentText.slice(0, cursorPos);
    const right = currentText.slice(cursorPos);

    const updated = options.map((opt, idx) => (idx === i ? { ...opt, text: left } : opt));

    const newOptions: MCOption[] = [
      ...updated.slice(0, i + 1),
      {
        text: right,
        isCorrect: false,
        isChecked: false,
      },
      ...updated.slice(i + 1),
    ];
    // updateOptionsBlock(newOptions);
    updateOptions(newOptions, i + 1);

    focusInput(i + 1, 0);
  };

  const handleBackspaceKeydown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const input = e.currentTarget;
    const isEmpty = input.value === '';
    if (isEmpty) {
      deleteFocusedOption(i);
      return;
    }

    const isNotMarkingText = input.selectionEnd === 0;
    const isCursorAtStart = input.selectionStart === 0;
    const hasText = input.value.length > 0;

    const shouldDeleteAndMerge = isNotMarkingText && isCursorAtStart && hasText;
    if (shouldDeleteAndMerge) {
      if (i > 0) {
        const mergedText = options[i - 1].text + options[i].text;
        const updated = options
          .filter((_, index) => index !== i)
          .map((opt, index) => {
            if (index === i - 1) {
              return { ...opt, text: mergedText };
            }
            return opt;
          });

        updateOptions(updated);

        const cursorPositionOnMergePoint = mergedText.slice(0, -input.value.length).length;

        focusInput(i - 1, cursorPositionOnMergePoint);
      }
    }
  };

  const handleArrowKeydown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const canMoveUp = i > 0;
    const canMoveDown = i < options.length - 1;
    if (e.key === 'ArrowUp' && canMoveUp) {
      focusInput(i - 1);
    } else if (e.key === 'ArrowDown' && canMoveDown) {
      focusInput(i + 1);
    }
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        handleEnterKeyDown(e);
        break;
      case 'Backspace':
        handleBackspaceKeydown(e);
        break;
      case 'ArrowUp':
        e.preventDefault();
        handleArrowKeydown(e);
        break;
      case 'ArrowDown':
        e.preventDefault();
        handleArrowKeydown(e);
        break;
      default:
        break;
    }
  };

  return (
    <HStack key={i} alignItems="center" gap="15px">
      <HStack gap="10px" width="70%">
        <Checkbox.Root
          checked={opt.isCorrect}
          onCheckedChange={() => toggleOption(i)}
          colorScheme="green"
          gap=".8rem"
          size="lg"
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control
            borderColor="border.accent"
            borderRadius="s"
            width="24px"
            height="24px"
            _checked={{
              backgroundColor: 'background.accent',
              border: 'none',
              color: 'text.onAccent',
              _hover: {
                backgroundColor: 'background.accent.hover',
                border: 'none',
              },
            }}
          />
        </Checkbox.Root>
        <Input
          value={opt.text}
          ref={(el) => {
            optionRefs.current[i] = el;
          }}
          onChange={(e) => updateOptionText(i, e.target.value)}
          onKeyDown={(e) => {
            handleKeydown(e);
          }}
          placeholder={`Option ${i + 1}`}
          style={{
            fieldSizing: 'content',
          }}
          size="mdNoInset"
          borderColor="border.accent"
          backgroundColor="background.secondary"
          borderRadius="10px"
          width="100%"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          boxSizing="border-box"
          _placeholder={{
            color: 'text.quaternary',
          }}
          _focus={{
            borderColor: 'border.accent.active',
            boxShadow: '0 0 0 1px var(--chakra-colors-green-primary)',
          }}
          _hover={{
            borderColor: 'border.accent.active',
            transition: 'all 0.2s ease-in-out',
          }}
        />
      </HStack>

      <NovaIconButton
        aria-label="Antwortmöglichkeit löschen"
        variant="primary"
        size="m"
        onClick={() => {
          deleteFocusedOption(i);
        }}
        boxSize="18px"
        fontSize="18px"
        lineHeight="0"
        minWidth="0px"
        icon={DeleteIcon}
        iconProps={{ fill: 'transparent', color: 'icon.quaternary' }}
      />
    </HStack>
  );
};
